import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Church } from '@fellowship/shared'
import { ArrowRight } from 'lucide-react'
import { Icons } from './ui/Icons'
import { useAuthStore } from '../store/auth'
import { useAdminAutoScrape, useAdminEnrichChurch } from '../hooks/useAdmin'

const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

function formatDistance(meters: number) {
  const miles = meters / 1609.34
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`
}

export function ChurchCard({
  church,
  selected,
  onClick,
}: {
  church: Church
  selected?: boolean
  onClick?: () => void
}) {
  const user = useAuthStore((s) => s.user)
  const isSuperAdmin = user?.role === 'super_admin'
  const autoScrape = useAdminAutoScrape()
  const enrichChurch = useAdminEnrichChurch()
  const [scrapeMsg, setScrapeMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [enrichMsg, setEnrichMsg] = useState<{ ok: boolean; text: string } | null>(null)

  function handleFindEvents(e: React.MouseEvent) {
    e.stopPropagation()
    setScrapeMsg(null)
    autoScrape.mutate({ churchId: church.id }, {
      onSuccess: (res) => setScrapeMsg({
        ok: true,
        text: `${res.data.eventsCreated} created, ${res.data.eventsSkipped} skipped`,
      }),
      onError: (err) => setScrapeMsg({
        ok: false,
        text: err instanceof Error ? err.message : 'Scrape failed',
      }),
    })
  }

  function handleEnrich(e: React.MouseEvent) {
    e.stopPropagation()
    setEnrichMsg(null)
    enrichChurch.mutate(church.id, {
      onSuccess: (res) => {
        const updated = res.data.updated
        setEnrichMsg({
          ok: true,
          text: updated.length ? `Updated: ${updated.join(', ')}` : 'Already complete',
        })
      },
      onError: (err) => setEnrichMsg({
        ok: false,
        text: err instanceof Error ? err.message : 'Enrich failed',
      }),
    })
  }

  return (
    <article
      onClick={onClick}
      data-church-id={church.id}
      style={{
        display: 'flex',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 16,
        cursor: 'pointer',
        background: selected ? 'var(--primary-soft)' : 'var(--surface)',
        border: `${selected ? 2 : 1}px solid ${selected ? 'var(--primary)' : 'var(--line-soft)'}`,
        margin: selected ? '-1px 0' : 0,
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Logo / initial */}
      {church.logoUrl ? (
        <img
          src={church.logoUrl}
          alt={`${church.name} logo`}
          style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            flexShrink: 0,
            background: GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 22,
            fontWeight: 700,
            fontFamily: 'Newsreader, serif',
          }}
        >
          {church.name.charAt(0)}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          {church.verified && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 18,
                padding: '0 7px',
                borderRadius: 999,
                background: '#DCFCE7',
                border: '1px solid #86EFAC',
                fontSize: 10.5,
                color: '#166534',
                fontWeight: 600,
              }}
            >
              Verified
            </span>
          )}
          {church.distanceMeters !== undefined && (
            <span
              style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--muted)', fontWeight: 500 }}
            >
              {formatDistance(church.distanceMeters)}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--ink)',
            lineHeight: 1.2,
            marginBottom: 3,
          }}
        >
          <Link
            to={`/churches/${church.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {church.name}
          </Link>
        </div>

        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: church.upcomingEventCount > 0 ? 4 : 6 }}>
          {[
            church.denomination?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            church.city,
            church.state,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>

        {church.upcomingEventCount > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--primary)', fontWeight: 500, marginBottom: 6 }}>
            {church.upcomingEventCount} upcoming {church.upcomingEventCount === 1 ? 'event' : 'events'}
          </div>
        )}

        {isSuperAdmin && (
          <div style={{ marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                onClick={handleFindEvents}
                disabled={autoScrape.isPending}
                style={{
                  height: 24, padding: '0 10px', borderRadius: 6,
                  fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  cursor: autoScrape.isPending ? 'default' : 'pointer',
                  border: '1px solid var(--line)',
                  background: autoScrape.isPending ? 'var(--surface)' : 'var(--bg)',
                  color: autoScrape.isPending ? 'var(--muted)' : 'var(--ink-2)',
                }}
              >
                {autoScrape.isPending ? '⟳ Finding…' : '⟳ Find Events'}
              </button>
              <button
                onClick={handleEnrich}
                disabled={enrichChurch.isPending}
                style={{
                  height: 24, padding: '0 10px', borderRadius: 6,
                  fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  cursor: enrichChurch.isPending ? 'default' : 'pointer',
                  border: '1px solid var(--line)',
                  background: enrichChurch.isPending ? 'var(--surface)' : 'var(--bg)',
                  color: enrichChurch.isPending ? 'var(--muted)' : 'var(--ink-2)',
                }}
              >
                {enrichChurch.isPending ? '✦ Enriching…' : '✦ Enrich'}
              </button>
            </div>
            {scrapeMsg && (
              <span style={{ fontSize: 11, fontWeight: 500, color: scrapeMsg.ok ? '#15803d' : '#dc2626' }}>
                {scrapeMsg.text}
              </span>
            )}
            {enrichMsg && (
              <span style={{ fontSize: 11, fontWeight: 500, color: enrichMsg.ok ? '#15803d' : '#dc2626' }}>
                {enrichMsg.text}
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {church.followerCount > 0 && (
            <span
              style={{
                fontSize: 11,
                color: 'var(--muted)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icons.users size={11} /> {church.followerCount}{' '}
              {church.followerCount === 1 ? 'follower' : 'followers'}
            </span>
          )}
          <Link
            to={`/churches/${church.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{ marginLeft: 'auto' }}
          >
            <button
              style={{
                height: 28,
                padding: '0 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'inherit',
                background: selected ? GRADIENT : 'transparent',
                color: selected ? '#fff' : 'var(--primary)',
                border: selected ? 'none' : '1.5px solid var(--primary)',
              }}
            >
              View <ArrowRight size={11} />
            </button>
          </Link>
        </div>
      </div>
    </article>
  )
}
